import { Edit03, Minus, Plus, Trash02, Users01 } from "@untitledui/icons";
import { type ReactNode, useState } from "react";
import { Button } from "@/components/base/buttons/button";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cx } from "@/utils/cx";
import { pluralize } from "@/utils/text";
import { type CohortOption, CohortSelectPanel, CohortSelectPopover } from "./cohort-select-panel";

/** The base scope's id. Every other scope id is a variation's. */
export const GLOBAL = "global";

/**
 * Shown under the cohort controls while the merchant has none to select. Cohorts are created
 * on their own page, which opens in a new tab so a half-built automation is not lost.
 */
function CohortsEmptyState({ merchantId }: { merchantId: string }) {
  return (
    <div className="relative flex flex-col gap-4 overflow-hidden rounded-xl border border-secondary bg-unbox-cream-50 p-6">
      {/* Illustrative cohort tags, not real data. Bleeds past the card's right edge. */}
      <img
        aria-hidden
        alt=""
        src="/cohorts-empty-state.png"
        className="pointer-events-none absolute top-5 -right-8 w-[196px] select-none"
      />

      <div className="flex max-w-[60%] flex-col gap-2">
        <p className="text-base leading-6 font-semibold text-primary">No Cohorts? Create One</p>
        <p className="text-sm leading-5 text-tertiary">
          You don&apos;t have any cohorts yet. Create one to start customizing
        </p>
      </div>

      <Button
        href={`/merchant/${merchantId}/cohorts`}
        target="_blank"
        rel="noopener noreferrer"
        color="secondary"
        size="sm"
        iconLeading={<Plus className="size-5" />}
        className="self-start"
      >
        Create New Cohort
      </Button>
    </div>
  );
}

/**
 * A variation's actions. The cohort picker sits in a submenu rather than a popover of its own, so
 * it opens beside the item that asked for it. It keeps the panel's batched selection: nothing is
 * applied until Save, which also closes the menu — the panel has nothing left to apply.
 */
function VariationMenu({
  label,
  options,
  selected,
  onChangeCohorts,
  onRemove,
}: {
  label: string;
  options: CohortOption[];
  selected: string[];
  onChangeCohorts: (cohortIds: string[]) => void;
  onRemove: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Dropdown.DotsButton aria-label={`Actions for ${label}`} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Users01 className="mr-2 size-4" />
            Edit cohorts
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-auto p-0">
            <CohortSelectPanel
              options={options}
              initialSelected={selected}
              onConfirm={(ids) => {
                onChangeCohorts(ids);
                setOpen(false);
              }}
            />
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="text-error-primary" onSelect={onRemove}>
          <Trash02 className="mr-2 size-4" />
          Delete variation
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export interface ScopeRailVariation {
  id: string;
  cohortIds: string[];
}

export interface AutomationScopeRailProps {
  merchantId: string;
  /** Why the cohort controls cannot be used, or null when they can. */
  inertReason: string | null;
  scopeId: string;
  onSelectScope: (id: string) => void;
  baseScopeName: string;
  baseReach: number;
  cohortCount: number;
  cohortOptions: CohortOption[];
  cohortNames: (ids: string[]) => string[];
  reachOf: (ids: string[]) => number;
  variations: ScopeRailVariation[];
  excludedCohortIds: string[];
  claimedCohortIds: Set<string>;
  onCreateVariation: () => void;
  onChangeVariationCohorts: (id: string, cohortIds: string[]) => void;
  onRemoveVariation: (id: string) => void;
  onChangeExcluded: (ids: string[]) => void;
}

/**
 * A cohort control that cannot be used, standing in for the working one and explaining itself on
 * hover. It carries no handler rather than a `disabled` attribute, which would suppress the
 * pointer events the tooltip needs. TooltipTrigger renders the button itself, so nothing may wrap
 * a second one around it.
 */
function InertControl({ reason, icon, label }: { reason: string; icon: ReactNode; label: string }) {
  return (
    <Tooltip title={reason} placement="right">
      <TooltipTrigger
        aria-disabled
        className="inline-flex cursor-not-allowed items-center gap-2 self-start text-sm leading-5 font-semibold text-gray-400"
      >
        {icon}
        {label}
      </TooltipTrigger>
    </Tooltip>
  );
}

export function AutomationScopeRail({
  merchantId,
  inertReason,
  scopeId,
  onSelectScope,
  baseScopeName,
  baseReach,
  cohortCount,
  cohortOptions,
  cohortNames,
  reachOf,
  variations,
  excludedCohortIds,
  claimedCohortIds,
  onCreateVariation,
  onChangeVariationCohorts,
  onRemoveVariation,
  onChangeExcluded,
}: AutomationScopeRailProps) {
  /** Cohorts this scope may take: its own, plus any no other scope has claimed. */
  const selectableFor = (ownIds: string[]) =>
    cohortOptions.filter((c) => ownIds.includes(c.id) || !claimedCohortIds.has(c.id));

  const scopeCard = ({
    id,
    name,
    reach,
    pill,
    menu,
    onSelect,
  }: {
    id: string;
    name: string;
    reach: ReactNode;
    pill?: string;
    menu?: ReactNode;
    onSelect?: () => void;
  }) => {
    const active = id === scopeId;
    return (
      <div
        key={id}
        className={cx(
          "flex items-start gap-2 rounded-xl px-4 py-3 transition-colors duration-100",
          active ? "bg-brand-100" : "bg-unbox-cream-50 hover:bg-unbox-cream-25",
        )}
      >
        <button
          type="button"
          onClick={onSelect}
          disabled={!onSelect}
          aria-pressed={active}
          className={cx(
            "flex min-w-0 flex-1 flex-col gap-0.5 text-left",
            onSelect ? "cursor-pointer" : "cursor-default",
          )}
        >
          <span className="text-sm leading-5 font-semibold break-words text-primary">{name}</span>
          <span className="flex min-w-0 flex-wrap items-center gap-1.5 text-[13px] leading-5 text-tertiary">
            <span>{reach}</span>
            {pill && (
              <>
                <span aria-hidden className="size-1 shrink-0 rounded-full bg-gray-400" />
                <span>{pill}</span>
              </>
            )}
          </span>
        </button>
        {menu && <span className="inline-flex shrink-0 items-center justify-center">{menu}</span>}
      </div>
    );
  };

  return (
    <aside className="relative z-20 flex w-[34%] max-w-[520px] min-w-[380px] shrink-0 flex-col">
      <div className="flex flex-1 flex-col gap-5">
        <div className="flex flex-col gap-2">
          {scopeCard({
            id: GLOBAL,
            name: baseScopeName,
            reach: (
              <>
                Applies to <span className="font-semibold text-secondary">{baseReach.toLocaleString("en-US")}</span>{" "}
                creators
              </>
            ),
            pill: pluralize(cohortCount, "cohort", "cohorts"),
            onSelect: () => onSelectScope(GLOBAL),
          })}

          {variations.map((v) =>
            scopeCard({
              id: v.id,
              name: v.cohortIds.length > 0 ? cohortNames(v.cohortIds).join(", ") : "New Variation",
              reach: (
                <>
                  Applies to{" "}
                  <span className="font-semibold text-secondary">{reachOf(v.cohortIds).toLocaleString("en-US")}</span>{" "}
                  creators
                </>
              ),
              pill: v.cohortIds.length > 0 ? pluralize(v.cohortIds.length, "cohort", "cohorts") : undefined,
              onSelect: () => onSelectScope(v.id),
              menu: (
                <VariationMenu
                  label={cohortNames(v.cohortIds).join(", ") || "new variation"}
                  options={selectableFor(v.cohortIds)}
                  selected={v.cohortIds}
                  onChangeCohorts={(ids) => onChangeVariationCohorts(v.id, ids)}
                  onRemove={() => onRemoveVariation(v.id)}
                />
              ),
            }),
          )}
        </div>

        {inertReason ? (
          <InertControl
            reason={inertReason}
            icon={<Plus className="size-[18px]" aria-hidden />}
            label="Create variation for specific Cohort"
          />
        ) : (
          <button
            type="button"
            onClick={onCreateVariation}
            className="inline-flex cursor-pointer items-center gap-2 self-start text-sm leading-5 font-semibold text-brand-700 transition-colors duration-100 hover:text-brand-800"
          >
            <Plus className="size-[18px]" aria-hidden />
            Create variation for specific Cohort
          </button>
        )}

        {excludedCohortIds.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className="shrink-0 text-xs font-semibold tracking-wide text-tertiary uppercase">Excluded</span>
              <span aria-hidden className="h-px flex-1 bg-gray-200" />
            </div>
            {scopeCard({
              id: "excluded",
              name: cohortNames(excludedCohortIds).join(", "),
              reach: (
                <>
                  Won&apos;t apply to{" "}
                  <span className="font-semibold text-secondary">
                    {reachOf(excludedCohortIds).toLocaleString("en-US")}
                  </span>{" "}
                  creators
                </>
              ),
              pill: excludedCohortIds.length > 1 ? pluralize(excludedCohortIds.length, "cohort", "cohorts") : undefined,
              menu: (
                <CohortSelectPopover
                  align="end"
                  trigger={
                    <button
                      type="button"
                      aria-label="Edit excluded cohorts"
                      className="flex size-6 cursor-pointer items-center justify-center rounded p-1.5 text-tertiary hover:bg-secondary"
                    >
                      <Edit03 className="size-4" />
                    </button>
                  }
                  options={selectableFor(excludedCohortIds)}
                  initialSelected={excludedCohortIds}
                  onConfirm={onChangeExcluded}
                />
              ),
            })}
          </div>
        )}

        {inertReason ? (
          <InertControl
            reason={inertReason}
            icon={<Minus className="size-[18px]" aria-hidden />}
            label="Exclude specific cohort from automation"
          />
        ) : (
          <CohortSelectPopover
            trigger={
              <button
                type="button"
                className="inline-flex cursor-pointer items-center gap-2 self-start text-sm leading-5 font-semibold text-brand-700 transition-colors duration-100 hover:text-brand-800"
              >
                <Minus className="size-[18px]" aria-hidden />
                Exclude specific cohort from automation
              </button>
            }
            options={cohortOptions.filter((c) => !claimedCohortIds.has(c.id))}
            onConfirm={(ids) => onChangeExcluded([...new Set([...excludedCohortIds, ...ids])])}
          />
        )}

        {cohortCount === 0 && <CohortsEmptyState merchantId={merchantId} />}
      </div>
    </aside>
  );
}
